import React, { useState } from "react";
import toast from "react-hot-toast";

// 🟢 NEW: Haptic Feedback Engine
const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

const ProductCard = ({ product, cart, setCart }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false); 

  // 1. Find if item is in cart
  const cartItem = cart.find((item) => item._id === product._id);
  const quantity = cartItem ? cartItem.quantity : 0;

  // 2. Calculate Discount
  const discount = product.mrp 
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100) 
    : 0;

  // 3. Handle Add/Remove (WITH STOP PROPAGATION, STOCK CHECK & HAPTICS)
  const updateQty = (e, change) => {
    e.stopPropagation(); // Prevents opening the Popup

    // Stock Validation
    if (change > 0 && product.stock <= quantity) {
      vibrate([100, 50, 100]); // 📳 Error double-vibrate
      return toast.error(`Only ${product.stock} items left in stock!`);
    }

    let newCart;
    if (quantity === 0 && change === 1) {
      // Double check stock before adding new item
      if (product.stock <= 0) {
        vibrate([100, 50, 100]); // 📳 Error double-vibrate
        return toast.error("Item is Out of Stock!");
      }

      newCart = [...cart, { ...product, quantity: 1 }];
      vibrate(50); // 📳 Success! Firm tap for adding to cart
      toast.success(
        <div className="flex items-center gap-2">
           <img src={product.image} className="w-8 h-8 rounded-md object-cover" alt="" />
           <span className="text-sm font-bold text-gray-700">Added {product.name}</span>
        </div>,
        { style: { borderRadius: '12px', background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' } }
      );
    } else {
      newCart = cart.map((item) =>
        item._id === product._id
          ? { ...item, quantity: Math.max(0, item.quantity + change) }
          : item
      ).filter(item => item.quantity > 0);
      
      // 📳 Light tap for + or -
      if (change > 0) vibrate(50); // Firm tap for increasing
      if (change < 0) vibrate(30); // Lighter tap for decreasing
    }
    
    // 🟢 Update React State
    setCart(newCart);
    
    // 🟢 CRITICAL FIX: Save directly to memory instantly!
    localStorage.setItem("cart", JSON.stringify(newCart));
  };

  const handleLike = (e) => {
    e.stopPropagation(); 
    vibrate(20); // 📳 Very light tap for liking
    setIsLiked(!isLiked);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 relative group overflow-hidden flex flex-col h-full">
      
      {/* 🟢 1. BEST SELLER BADGE */}
      {product.isBestSeller && (
        <div className="absolute top-0 left-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] font-extrabold px-3 py-1 rounded-br-xl z-20 shadow-sm flex items-center gap-1">
          ★ Best Seller
        </div>
      )}

      {/* 🟢 2. WISHLIST HEART */}
      <button 
        onClick={handleLike}
        className="absolute top-2 right-2 z-20 bg-white/60 backdrop-blur-sm p-2 rounded-full shadow-sm hover:bg-white transition-all active:scale-90"
      >
        <span className={`text-lg transition-colors ${isLiked ? "text-red-500 scale-110" : "text-gray-400 grayscale"}`}>
          {isLiked ? "❤️" : "🤍"}
        </span>
      </button>

      {/* 🟢 3. IMAGE AREA (Sitting perfectly at ~60% ratio) */}
      <div className="relative h-36 md:h-44 w-full bg-gray-100 flex items-center justify-center overflow-hidden">
        {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
        <img 
          src={product.image} 
          alt={product.name} 
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          className={`h-full w-full object-cover transform group-hover:scale-110 transition-all duration-700 ease-in-out ${
            imageLoaded ? "opacity-100 blur-0" : "opacity-0 blur-md"
          }`}
        />
        
        {/* Out of Stock Overlay */}
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-sm z-30 backdrop-blur-[2px]">
            SOLD OUT
          </div>
        )}

        {discount > 0 && (
          <div className="absolute bottom-2 left-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md z-10 shadow-sm">
            {discount}% OFF
          </div>
        )}
      </div>

      {/* 🟢 4. INFO AREA (Sitting perfectly at ~40% ratio) */}
      <div className="p-3 flex flex-col flex-1 relative">
        {/* Veg icon */}
        <div className="absolute top-3 right-3 border border-green-600 p-[1px] w-3 h-3 flex items-center justify-center rounded-[2px]">
          <div className="w-1.5 h-1.5 bg-green-600 rounded-full"></div>
        </div>

        <h3 className="font-bold text-gray-800 text-sm md:text-[15px] leading-tight line-clamp-2 flex-1 mb-1 pr-4">
          {product.name}
        </h3>
        <p className="text-xs text-gray-500 mb-3 font-medium">{product.category}</p>

        <div className="flex items-center gap-2 mb-3">
          <span className="font-extrabold text-gray-900 text-lg">₹{product.price}</span>
          {product.mrp && product.mrp > product.price && (
            <span className="text-xs text-gray-400 line-through decoration-gray-400">₹{product.mrp}</span>
          )}
        </div>

        {/* SMART ACTION BUTTON */}
        <div className="mt-auto relative z-30"> 
          {product.stock <= 0 ? (
             <button disabled className="w-full bg-gray-100 text-gray-400 font-bold py-2.5 rounded-xl text-xs cursor-not-allowed border border-gray-200">
               Out of Stock
             </button>
          ) : quantity === 0 ? (
            <button 
              onClick={(e) => updateQty(e, 1)}
              className="w-full bg-green-50 text-green-700 border border-green-200 font-bold py-2.5 rounded-xl text-sm hover:bg-green-600 hover:text-white hover:border-green-600 transition-all active:scale-95 shadow-sm uppercase tracking-wide"
            >
              ADD
            </button>
          ) : (
            <div className="flex items-center justify-between bg-green-600 text-white rounded-xl shadow-lg shadow-green-100 overflow-hidden h-10">
              <button 
                onClick={(e) => updateQty(e, -1)}
                className="w-10 h-full flex items-center justify-center hover:bg-green-700 active:bg-green-800 text-lg font-bold transition-colors"
              >
                −
              </button>
              <span className="font-bold text-sm min-w-[20px] text-center">{quantity}</span>
              <button 
                onClick={(e) => updateQty(e, 1)}
                className="w-10 h-full flex items-center justify-center hover:bg-green-700 active:bg-green-800 text-lg font-bold transition-colors"
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;